import { useState } from "react"

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"

import { type AdminPlugin, type PluginKind, pluginsApi } from "@/api/plugins-api"
import { Plug, Plus } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { CollapsibleManagementSection } from "@/features/admin-dashboard/components/collapsible-management-section"

import { PluginAccessEditor } from "./plugin-access-editor"
import { PluginDefinitionEditor } from "./plugin-definition-editor"
import { PluginMigration } from "./plugin-migration"
import { pluginErrorCode, pluginErrorMessage } from "./plugin-utils"
import { usePluginQueryKey, usePluginSessionGeneration } from "./use-plugin-query-key"

export function PluginsAdmin() {
  const generation = usePluginSessionGeneration()
  return <PluginsAdminContent key={generation} />
}

function PluginsAdminContent() {
  const { t } = useTranslation()
  const pluginKey = usePluginQueryKey()
  const client = useQueryClient()
  const [createOpen, setCreateOpen] = useState(false)
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [kind, setKind] = useState<PluginKind>("openapi")
  const [templateId, setTemplateId] = useState("custom")
  const [selected, setSelected] = useState<AdminPlugin | null>(null)
  const [deleting, setDeleting] = useState<AdminPlugin | null>(null)
  const [tab, setTab] = useState<"definition" | "access">("definition")
  const catalog = useQuery({ queryKey: pluginKey("admin"), queryFn: pluginsApi.adminList })
  const templates = useQuery({ queryKey: pluginKey("templates"), queryFn: pluginsApi.templates })
  const create = useMutation({
    mutationFn: () =>
      templateId === "custom"
        ? pluginsApi.create({ name, description, plugin_kind: kind })
        : pluginsApi.instantiate(templateId),
    onSuccess: async (data) => {
      setCreateOpen(false)
      setSelected(data.plugin)
      setTab("definition")
      setName("")
      setDescription("")
      await client.invalidateQueries({ queryKey: pluginKey() })
    },
  })
  const toggle = useMutation({
    mutationFn: (enabled: boolean) =>
      pluginsApi.setEnabled(enabled, catalog.data!.settings.revision),
    onSuccess: () => client.invalidateQueries({ queryKey: pluginKey() }),
  })
  const remove = useMutation({
    mutationFn: () => pluginsApi.delete(deleting!),
    onSuccess: async () => {
      setDeleting(null)
      await client.invalidateQueries({ queryKey: pluginKey() })
    },
  })
  const error = create.error ?? toggle.error ?? remove.error ?? catalog.error
  return (
    <CollapsibleManagementSection
      icon={<Plug />}
      id="plugin-store"
      storageKey="admin-plugin-store"
      title={t("plugins.adminTitle")}
      headerActions={
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 size-4" />
          {t("plugins.create")}
        </Button>
      }
    >
      <div className="space-y-4" data-testid="plugins-admin">
        <PluginMigration />
        <p className="text-muted-foreground text-sm">{t("plugins.adminDescription")}</p>
        {catalog.data && (
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={catalog.data.settings.enabled}
              disabled={toggle.isPending}
              onCheckedChange={(value) => toggle.mutate(value === true)}
            />
            {t("plugins.enableStore")}
          </label>
        )}
        {error && (
          <p className="text-destructive text-sm" role="alert">
            {pluginErrorMessage(t, pluginErrorCode(error))}
          </p>
        )}
        {catalog.isPending && <p role="status">{t("plugins.loading")}</p>}
        <div className="divide-y rounded-lg border">
          {catalog.data?.items.map((plugin) => (
            <div key={plugin.id} className="flex flex-wrap items-center justify-between gap-3 p-3">
              <div>
                <p className="font-medium">{plugin.name}</p>
                <p className="text-muted-foreground text-xs">
                  {plugin.plugin_kind.toUpperCase()} ·{" "}
                  {plugin.enabled ? t("plugins.enabled") : t("plugins.disabled")}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setSelected(plugin)
                    setTab("definition")
                  }}
                >
                  {t("plugins.definition")}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setSelected(plugin)
                    setTab("access")
                  }}
                >
                  {t("plugins.access")}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    remove.reset()
                    setDeleting(plugin)
                  }}
                >
                  {t("plugins.delete")}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
      <Dialog
        open={Boolean(deleting)}
        onOpenChange={(open) => {
          if (!open) setDeleting(null)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("plugins.deleteTitle", { name: deleting?.name })}</DialogTitle>
            <DialogDescription>{t("plugins.deleteDescription")}</DialogDescription>
          </DialogHeader>
          <Button disabled={remove.isPending} variant="destructive" onClick={() => remove.mutate()}>
            {t("plugins.delete")}
          </Button>
          {remove.error && <p role="alert">{t("plugins.error")}</p>}
        </DialogContent>
      </Dialog>
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("plugins.create")}</DialogTitle>
            <DialogDescription>{t("plugins.createDescription")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label>{t("plugins.startFrom")}</Label>
            <Select value={templateId} onValueChange={setTemplateId}>
              <SelectTrigger aria-label={t("plugins.startFrom")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="custom">{t("plugins.customDefinition")}</SelectItem>
                {templates.data?.templates.map((template) => (
                  <SelectItem key={template.id} value={template.id}>
                    {template.name} · v{template.version}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Label htmlFor="new-plugin-name">{t("plugins.name")}</Label>
            <Input
              id="new-plugin-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
            <Label htmlFor="new-plugin-description">{t("plugins.description")}</Label>
            <Textarea
              id="new-plugin-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
            <Label>{t("plugins.kind")}</Label>
            <Select value={kind} onValueChange={(value) => setKind(value as PluginKind)}>
              <SelectTrigger aria-label={t("plugins.kind")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="openapi">Custom API / OpenAPI</SelectItem>
                <SelectItem value="mcp">MCP</SelectItem>
                <SelectItem value="native">{t("plugins.tool")}</SelectItem>
              </SelectContent>
            </Select>
            <Button
              disabled={(templateId === "custom" && !name.trim()) || create.isPending}
              onClick={() => create.mutate()}
            >
              {t("plugins.create")}
            </Button>
            {create.error && <p role="alert">{t("plugins.error")}</p>}
          </div>
        </DialogContent>
      </Dialog>
      <Dialog
        open={Boolean(selected)}
        onOpenChange={(open) => {
          if (!open) setSelected(null)
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selected?.name}</DialogTitle>
            <DialogDescription>
              {tab === "definition" ? t("plugins.definition") : t("plugins.access")}
            </DialogDescription>
          </DialogHeader>
          {selected &&
            (tab === "definition" ? (
              <PluginDefinitionEditor
                key={selected.id}
                plugin={selected}
                onSaved={() => setSelected(null)}
              />
            ) : (
              <PluginAccessEditor
                key={selected.id}
                plugin={selected}
                onSaved={() => setSelected(null)}
              />
            ))}
        </DialogContent>
      </Dialog>
    </CollapsibleManagementSection>
  )
}
